import React, { useMemo } from 'react';
import classnames from 'classnames';
import LinkButton from 'components/uiLibrary/LinkButton';
import Typography from 'components/uiLibrary/Typography';
import CommonTrans from 'components/Globals/CommonTrans';
import { COMPONENTS } from 'components/Globals/Analytics/constants';

import useNavigate from 'utils/hooks/useNavigate';
import { useDialogs } from 'utils/hooks/useDialogs';
import { getProductionDatesGroupedByVenues } from 'utils/productions';
import { getCityCountry } from 'utils/globals';
import { useTranslation } from 'src/i18n';
import { createDate } from 'utils/date';

import { TP } from 'constants/index';
import { ENTITY_TYPES, DEPRECATED_ENTITY_TYPES } from 'operabase-router/constants';
import Dates from './Dates';

import classes from './VenuesAndDates.module.scss';

const VenueContent = ({ venue, trackingData, isLivestream, production }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const navigate = useNavigate();
  const linkProps = navigate.getLinkProps({ entityType: DEPRECATED_ENTITY_TYPES.VENUE, entity: venue });
  const isFutureProduction = createDate(new Date()).isBefore(production?.maxDate);

  const location = getCityCountry(venue);

  if (isLivestream) {
    return null;
  }

  return (
    <div className={classes.venueContent}>
      <div className={classes.venueContent__cityCountry}>
        <Typography size={12} italic className={classes.label}>
          {t(`${TP}.FN_CITY_COUNTRY`)}
        </Typography>
        <Typography weight="bold" size={12} className={classes.text}>
          {location || '-'}
        </Typography>
      </div>
      <div className={classes.venueContent__venueName}>
        <Typography size={12} italic className={classes.label}>
          {t(`${TP}.m_VENUE`)}
        </Typography>
        <Typography size={12} className={classes.text} weight="bold">
          {venue?.name ? (
            <LinkButton
              isLink
              variant="text"
              {...linkProps}
              styles={{ root: classes.name }}
              target="_blank"
              trackingData={{
                ...trackingData,
                entityId: venue?.id,
                entityName: venue?.name,
                entityType: ENTITY_TYPES.ORGANIZATION,
                ...(venue?.organizationType?.slug && { organisationType: venue?.organizationType?.slug }),
              }}
            >
              {venue?.name}
            </LinkButton>
          ) : (
            `${isFutureProduction ? t(`${TP}.FN_VENUE_NOT_ANNOUNCED`) : t(`${TP}.FN_VENUE_NOT_AVAILABLE`)}`
          )}
        </Typography>
      </div>
      {venue?.stage?.name && (
        <div className={classes.venueContent__stage}>
          <Typography size={12} color="secondary" italic className={classes.label}>
            {t(`${TP}.m_ROOM`)}
          </Typography>
          <Typography size={12} className={classes.text} color="secondary">
            {venue?.stage?.name}
          </Typography>
        </div>
      )}
    </div>
  );
};

const VenuesAndDates = ({ production, entityId, trackingData = {}, entityType }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const venuesDates = useMemo(
    () =>
      getProductionDatesGroupedByVenues({ production, entityId, entityType })?.filter(dates => !dates?.isLivestream),
    [entityType, production, entityId],
  );

  const { isJumpInSelectProductionsModalOpen } = useDialogs();

  return (
    <div
      className={classnames({
        [classes.disableBlock]: !!isJumpInSelectProductionsModalOpen,
      })}
    >
      <ul className={classes.venuesDates}>
        {venuesDates?.map(({ venue, dates, isLivestream }, index) => (
          <li className={classes.venueItem} key={`${venue?.id}-${venue?.stage?.id}-${index}`}>
            <VenueContent
              venue={venue}
              isLivestream={isLivestream}
              trackingData={{ component: COMPONENTS.VENUES, ...trackingData }}
              production={production}
            />
            <div className={classes.datesList}>
              <Typography size={12} italic className={classes.label}>
                {t(`${TP}.FN_PERF_DATES`)}
              </Typography>
              <div>
                {Object.keys(dates).map((monthYear, datesIndex) => {
                  const [month, year] = monthYear.split(' ');

                  return (
                    <Dates
                      key={`${monthYear}-${datesIndex}`}
                      year={year}
                      month={month}
                      dates={dates[monthYear]}
                      isCancelled={production?.isCancelled}
                      shouldShowYear={datesIndex === 0}
                      entity={production}
                      trackingData={{
                        component: COMPONENTS.VENUES,
                        ...trackingData,
                      }}
                    />
                  );
                })}
              </div>
            </div>
          </li>
        ))}
      </ul>
      <Typography variant="p" size="12" italic className={classes.venuesDatesTitle}>
        <CommonTrans
          i18nKey={`${TP}.FN_ARTIST_PERFORMED_DATES_VENUES`}
          ns="NS_CASTING_TOOL"
          components={{
            b: <b />,
          }}
        />
      </Typography>
    </div>
  );
};

export default VenuesAndDates;
